import React, { useEffect } from 'react'
import { View } from 'react-native'
import { connect, ConnectedProps } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { includes } from 'lodash-es'
import styled from 'styled-components/native'

import { AlertBannerTypes } from '@lyrahealth-inc/shared-app-logic/src/features/alerts/AlertBannerTypes'
import { CustomerName } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customer'
import {
  COMMON_MIXPANEL_EVENTS,
  ERROR_BANNER_PROPERTIES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { formatMessageHelper } from '@lyrahealth-inc/shared-app-logic/src/features/intl/utils/formatMessageHelper'
import { getStringFromMessage } from '@lyrahealth-inc/shared-app-logic/src/features/intl/utils/getStringFromMessage'
import { STATUS_CODES_TO_NOT_LOG } from '@lyrahealth-inc/shared-app-logic/src/features/sentry/utils'
import { AlertBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/alertBanner/AlertBanner'

import { BANNER_TYPE, closeBanner, setBannerPropsAndStatus } from './banner-actions'
import {
  getApiErrorMessageProps,
  getBannerIncludeX,
  getBannerIsOpen,
  getBannerMessage,
  getBannerType,
} from '../../../data/banner/bannerSelectors'
import { getCustomerName } from '../../../data/customer/customerSelectors'
import { trackEventWithObj } from '../../../data/mixpanel'
import { isInLoginFlow } from '../../../features/login/loginUtils'
import { isInRegisterFlow } from '../../../features/register/data/registerUtils'
import { ELIGIBILITY_EXPIRATION__WALMART_IN_MINS } from '../../constants/registrationFormConstants'
import { sentryCaptureException } from '../../utils/sentryUtils'

type BannerViewProps = ConnectedProps<typeof connector>

// Styled wrapper to apply custom styles based on page context
const BannerWrapper = styled(View)<{
  $isAuthPage: boolean
  $hasCloseButton: boolean
  $isProfilePage: boolean
}>(({ $isAuthPage, $hasCloseButton, $isProfilePage }) => ({
  ...($isAuthPage && { marginBottom: 0, order: -1 }),
  ...(($hasCloseButton || $isProfilePage) && { marginBottom: 0 }),
}))

const ERROR_MESSAGE_QUERY_PARAM = 'errormsg'

const BannerView = ({
  message,
  bannerType,
  customerName,
  isOpen,
  setBannerPropsAndStatus,
  includeX = false,
  closeBanner,
  errorMessageProps,
  trackEventWithObj,
}: BannerViewProps) => {
  const { search, pathname } = useLocation()
  const queryParams = new URLSearchParams(search)
  const errormsg = queryParams.get(ERROR_MESSAGE_QUERY_PARAM)

  useEffect(() => {
    if (errormsg) {
      setBannerPropsAndStatus({
        bannerType: BANNER_TYPE.DANGER,
        message: errormsg,
        shouldAutomaticallyClose: false,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errormsg])

  useEffect(() => {
    if (isOpen && bannerType === BANNER_TYPE.DANGER) {
      const lyraErrorMessage = getStringFromMessage(message)

      const viewErrorBannerEventProp = {
        event: COMMON_MIXPANEL_EVENTS.ERROR_BANNER,
        ...(lyraErrorMessage && { [ERROR_BANNER_PROPERTIES.LYRA_ERROR_MESSAGE]: lyraErrorMessage }),
      }
      console.debug(
        `Sending ${COMMON_MIXPANEL_EVENTS.ERROR_BANNER} event to mixpanel with props : ${errorMessageProps}`,
      )
      trackEventWithObj(
        errorMessageProps
          ? {
              ...viewErrorBannerEventProp,
              [ERROR_BANNER_PROPERTIES.ENDPOINT_METHOD]: errorMessageProps?.requestMethod ?? '',
              [ERROR_BANNER_PROPERTIES.ENDPOINT_URL]: errorMessageProps?.requestUrl ?? '',
              [ERROR_BANNER_PROPERTIES.HTTP_ERROR_CODE]: errorMessageProps?.statusCode ?? '',
              [ERROR_BANNER_PROPERTIES.ERROR_MESSAGE]: errorMessageProps?.errorMessage ?? '',
            }
          : viewErrorBannerEventProp,
      )
      if (!includes(STATUS_CODES_TO_NOT_LOG, errorMessageProps?.statusCode)) {
        const sentryError = new Error(errorMessageProps?.errorMessage ?? '')
        sentryError.name = `Failed request - ${errorMessageProps?.requestUrl}`
        sentryCaptureException(sentryError, errorMessageProps ?? { source: 'banner-container.tsx' })
      }
    }
  }, [bannerType, errorMessageProps, isOpen, message, trackEventWithObj])

  if (!message) {
    return null
  }

  const isRegisterPage = isInRegisterFlow(pathname)
  const isLoginPage = isInLoginFlow(pathname)
  const isProfilePage = pathname.includes('profile')
  const isAuthPage = isRegisterPage || isLoginPage

  // Map BANNER_TYPE to AlertBannerTypes
  const alertBannerStyle = bannerType as AlertBannerTypes

  return (
    <BannerWrapper $isAuthPage={isAuthPage} $hasCloseButton={includeX} $isProfilePage={isProfilePage}>
      <AlertBanner
        show={isOpen}
        minHeight='0px'
        style={alertBannerStyle}
        includeX={includeX}
        dismissHandler={() =>
          closeBanner({
            message,
            shouldDismissEligibilityCheck: true,
            ...(customerName == CustomerName.WALMART && {
              eapEligibilityBannerExpiration: ELIGIBILITY_EXPIRATION__WALMART_IN_MINS,
            }),
          })
        }
        contents={formatMessageHelper(message)}
      />
    </BannerWrapper>
  )
}

const mapStateToProps = ($$state: $TSFixMe) => {
  return {
    message: getBannerMessage($$state),
    bannerType: getBannerType($$state),
    isOpen: getBannerIsOpen($$state),
    includeX: getBannerIncludeX($$state),
    customerName: getCustomerName($$state),
    errorMessageProps: getApiErrorMessageProps($$state),
  }
}

const connector = connect(mapStateToProps, { setBannerPropsAndStatus, closeBanner, trackEventWithObj })
export default connector(BannerView)
